import { type ModuleApiInfo } from "@client/content/selectors";
import { FormRadioButtonGroup, type RadioOptionItem } from "@client/controls/Inputs/RadioButtonGroup/component";
import { type PargoPickupPoint } from "@common/components/Integrations/PargoPickup";
import FormFieldError from "@common/controls/Form/Field/Error";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import { identity } from "lodash";
import * as React from "react";
import { type IRef, type IWithValidate, withOnSubmit } from "../../hooks";
import { type Checkout } from "../../types";
import ParametersProvider, { type ParametersProviderChildrenProps } from "../ParametersProvider";
import PargoPickupShippingMethod from "./PargoPickup";

export type MapProps = {
  apiInfo: ModuleApiInfo;

  order: Checkout.Order;
  shippingRules: Checkout.ShippingRule[];
};

export interface ActionProps {
  onChange: (value: string | null) => void;
  onPickupPointChange: (data: PargoPickupPoint) => void;
}
export type Props = ActionProps & MapProps;

export default (props: Props) => {
  const i18n = useI18n();

  const [error, setError] = React.useState<string | null>(null);
  const methodRef = React.useRef<IWithValidate>(null);
  const ref = withOnSubmit(identity);

  const shippingRule = React.useMemo(
    () => props.shippingRules.find((x) => x.id === props.order.shippingRuleId) ?? null,
    [props.order.shippingRuleId, props.shippingRules]
  );

  const onPickupPointChange = React.useCallback(
    (data: PargoPickupPoint) => {
      props.onPickupPointChange(data);
      setError(null);
    },
    [props.onChange, setError]
  );

  const options = React.useMemo(
    (): RadioOptionItem[] =>
      props.shippingRules.map((x) => ({
        label: x.name,
        value: x.id
      })),
    [props.shippingRules]
  );

  React.useEffect(() => {
    if (options.length === 1) {
      setTimeout(() => {
        props.onChange(options[0].value);
      }, 0);
    }
  }, [options, props.onChange]);

  React.useImperativeHandle<any, IRef>(
    ref,
    () => ({
      getFinalValues: async () => {
        if (error) return Promise.resolve(null);

        return Promise.resolve({
          shippingRuleId: props.order.shippingRuleId
        });
      },
      validate: async () => {
        let error: string | null = null;
        if (!props.order.shippingRuleId && options.length) {
          error = i18n.errorRequiredField;
        }

        if (methodRef.current) {
          error = (await methodRef.current.validate()) as string;
        }

        setError(error);

        return error;
      }
    }),
    [ref, options, props.order.shippingRuleId, error]
  );

  const onShippingRuleChange = React.useCallback(
    (value: string) => {
      props.onChange(value);
      setError(null);
    },
    [props.onChange, setError]
  );

  const shippingRules = React.useMemo(() => sort(props.shippingRules, { key: "name" }), [props.shippingRules]);

  return (
    <div className="shipping_method">
      <h4>{i18n.shippingMethod}</h4>

      {shippingRules.length ? (
        <div>
          <FormRadioButtonGroup
            onChange={onShippingRuleChange}
            options={options}
            orientation="vertical"
            value={props.order.shippingRuleId}
          >
            {(item: RadioOptionItem) => {
              if (!shippingRule) return;

              if (item.value === shippingRule.id && shippingRule.app === "PargoPickup") {
                return (
                  <ParametersProvider apiInfo={props.apiInfo} integrationId={shippingRule.integrationId!}>
                    {(ps: ParametersProviderChildrenProps) => (
                      <PargoPickupShippingMethod
                        ref={methodRef}
                        onPickupPointChange={onPickupPointChange}
                        order={props.order}
                        parameters={ps.parameters}
                        shippingRule={shippingRule}
                      />
                    )}
                  </ParametersProvider>
                );
              }
            }}
          </FormRadioButtonGroup>

          {!!error && <FormFieldError error={error} name="shippingRuleId" />}
        </div>
      ) : (
        <small className="error field_error">{i18n.errorNoShippingMethod}</small>
      )}
    </div>
  );
};
